/**
 * atmosphereLevels.ts
 *
 * Thin translucent wireframe reference shells, one per real GFS pressure
 * level, placed at that level's global-mean standard-atmosphere altitude.
 * They share the exact same height-exaggeration factor as the terrain, so
 * terrain and atmosphere stay matched as requested.
 */

import * as THREE from "three";
import { GFS_PRESSURE_LEVELS_HPA, EARTH_RADIUS_UNITS, KM_PER_SCENE_UNIT, ATMOSPHERE_TOP_KM } from "../constants";
import { pressureToHeightM } from "../physics/standardAtmosphere";

export interface LevelShellHandles {
  group: THREE.Group;
  setHeightExaggeration(factor: number): void;
  setVisibleRange(minHpa: number, maxHpa: number): void;
}

export function createAtmosphereLevelShells(): LevelShellHandles {
  const group = new THREE.Group();
  const shells: Array<{ mesh: THREE.Mesh; hPa: number; baseAltKm: number }> = [];
  const sharedGeometry = new THREE.SphereGeometry(1, 48, 24);

  for (const hPa of GFS_PRESSURE_LEVELS_HPA) {
    const baseAltKm = pressureToHeightM(hPa) / 1000;
    const t = Math.min(1, baseAltKm / ATMOSPHERE_TOP_KM);
    // Low altitude -> blue-ish, high altitude -> violet/white, purely cosmetic.
    const color = new THREE.Color().setHSL(0.62 - 0.4 * t, 0.65, 0.6);
    const material = new THREE.MeshBasicMaterial({
      color,
      wireframe: true,
      transparent: true,
      opacity: 0.05,
      depthWrite: false
    });
    const mesh = new THREE.Mesh(sharedGeometry, material);
    mesh.renderOrder = 3;
    group.add(mesh);
    shells.push({ mesh, hPa, baseAltKm });
  }

  function setHeightExaggeration(factor: number): void {
    for (const shell of shells) {
      const radiusUnits = EARTH_RADIUS_UNITS + (shell.baseAltKm * factor) / KM_PER_SCENE_UNIT;
      shell.mesh.scale.setScalar(radiusUnits);
    }
  }

  function setVisibleRange(minHpa: number, maxHpa: number): void {
    const epsilon = 1e-4;
    for (const shell of shells) {
      shell.mesh.visible = shell.hPa >= minHpa - epsilon && shell.hPa <= maxHpa + epsilon;
    }
  }

  setHeightExaggeration(1);

  return { group, setHeightExaggeration, setVisibleRange };
}
